import toCamelCaseKeys from 'camelcase-keys';
import { getCookie, setCookie } from './sessionUtils';
import * as yup from 'yup';
import { FieldError, Merge, FieldErrorsImpl } from 'react-hook-form';
import { CustomFieldType } from 'types/types/Types';
import { ECaseFilter } from 'enum/ECaseFilter';

export type RHFMaybeFieldError =
  | FieldError
  | Merge<FieldError, FieldErrorsImpl<any>>
  | undefined

export const toFieldError = (err: RHFMaybeFieldError): FieldError | undefined => {
  const e = err as unknown as { type?: unknown } | undefined
  if (!e || typeof e !== 'object') return undefined
  return 'type' in e ? (err as FieldError) : undefined
}

export const enumToLabelValueList = (enumObj: Record<string, string>) => {
  return Object.entries(enumObj).map(([key, value]) => ({
    key: key,
    label: toTitleCase(value),
    value: key,
  }));
};

export const listToLabelValueList = (list: string[]) => {
  return list.map((value) => ({
    key: value,
    label: value,
    value: value,
  }));
};

export const isValidEmail = (email: string) => {
  const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
  return emailPattern.test(email);
};

export const stringToArray = (str: string): string[] => {
  return str
    .split(',')
    .map(s => s.trim())
    .filter(s => s.length > 0);
}

export const stringToDate = (dateString: string): Date | null => {
  if (!dateString) return null;

  if (dateString.includes('-')) {
    const [year, month, day] = dateString.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(Date.UTC(year, month - 1, day));
  }

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const cleaned = dateString.replace(',', '');
  const [monthStr, dayStr, yearStr] = cleaned.split(' ');

  const month = months.indexOf(monthStr.substring(0, 3));
  if (month === -1) return null;

  const day = parseInt(dayStr, 10);
  const year = Number(yearStr);
  if (isNaN(day) || isNaN(year)) return null;

  return new Date(Date.UTC(year, month, day));
};

// Accepts a Date or a dayjs value; forces local noon so extracting the
// calendar day via UTC getters (see dateToString) can't roll to an
// adjacent day for timezones offset from UTC.
export const getLocalDate = (date: any): Date => {
  const localDate = typeof date?.toDate === 'function' ? date.toDate() : new Date(date);
  localDate.setHours(12, 0, 0, 0);
  return localDate;
};

export const getLocalDateStr = (date: any): string => {
  return getLocalDate(date).toISOString().split('T')[0];
};

// Translates a quick filter (Today/This Week/etc.) into explicit local-date
// boundaries, so the backend's own (server-clock/UTC-based) filter=TODAY
// handling is bypassed entirely in favor of its timezone-agnostic
// start_date/end_date/before range filtering.
export const getQuickFilterDateParams = (
  filter: ECaseFilter
): { start_date?: string; end_date?: string; before?: string } => {
  const now = new Date();
  const todayStr = getLocalDateStr(now);

  switch (filter) {
    case ECaseFilter.TODAY:
      return { start_date: todayStr, end_date: todayStr };

    case ECaseFilter.THIS_WEEK: {
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay());
      return { start_date: getLocalDateStr(start), end_date: todayStr };
    }

    case ECaseFilter.THIS_MONTH: {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start_date: getLocalDateStr(start), end_date: todayStr };
    }

    case ECaseFilter.UPCOMING: {
      const tomorrow = new Date(now);
      tomorrow.setDate(now.getDate() + 1);
      return { start_date: getLocalDateStr(tomorrow) };
    }

    case ECaseFilter.OLDER:
      return { before: todayStr };

    default:
      return {};
  }
};

export function dateToString(date: Date): string {
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr',
    'May', 'Jun', 'Jul', 'Aug',
    'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const day = date.getUTCDate();
  const month = date.getUTCMonth();
  const year = date.getUTCFullYear();

  return `${months[month]} ${day}, ${year}`;
}

export function timeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (seconds < 60) return 'Just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;

  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;

  const years = Math.floor(days / 365);
  return `${years}y`;
}

export const listToStringList = (list: string[]): string => {
  if (Array.isArray(list) && list?.length > 0) {
    return list.join(', ');
  }
  return ''
}

export const enumToLabelValueListExp = (
  enumArray: { id: number | string; name: string }[]
) => {
  return enumArray.map(({ id, name }) => ({
    key: String(id),
    label: String(name),
    value: String(id),
  }));
};

export function formatPhoneNumber(phoneNumberString = '') {
  const curchr = phoneNumberString.length;
  let curval = phoneNumberString;
  if (curchr === 3 && curval.indexOf('(') <= -1) {
    curval = '(' + curval + ')' + '-';
  } else if (curchr === 4 && curval.indexOf('(') > -1) {
    curval = curval + ')-';
  } else if (curchr === 5 && curval.indexOf(')') > -1) {
    curval = curval + '-';
  } else if (curchr === 9) {
    curval = curval + '-';
  }
  return curval;
}

export function clearFormatedPhoneNumber(formatedPhoneNumber: any) {
  const cleaned = ('' + formatedPhoneNumber).replace(/\D/g, '');
  return cleaned;
}

export function getAge(birthDate: any) {
  if (birthDate !== undefined) {
    const date = birthDate && birthDate.split('-');

    const year = new Date().getFullYear() - new Date(date).getFullYear();
    const month = new Date().getMonth() + 1 - (new Date(date).getMonth() + 1);
    const day = new Date().getDate() - new Date(date).getDate();

    if (year > 0) {
      return `${year}y`;
    } else if (month > 0) {
      return `${month}m`;
    } else {
      return `${day}d`;
    }
  }
}

export function toCamelCase(object: any) {
  return toCamelCaseKeys(object, { deep: true });
}

export function getUser() {
  let parsedUser = null;
  const user = localStorage.getItem('user');
  if (user) {
    parsedUser = JSON.parse(unescape(user));
  }
  return parsedUser;
}

export function setUser(user: any) {
  setCookie('user', JSON.stringify(user));
  localStorage.setItem('@user', JSON.stringify(user));
}

export const userTheme = () => {
  if (
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  ) {
    localStorage.setItem('Theme', 'darkTheme');
  } else if (
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: light)').matches
  ) {
    localStorage.setItem('Theme', 'lightTheme');
  }
};

export const hexWithOpacity = (hex: string, opacity: number) =>
  hex + Math.round(opacity * 255).toString(16).padStart(2, '0');

export function toTitleCase(str: string) {
  if (!str) return '';
  return str
    .toLowerCase()
    .replace('-', ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .split('_')
    .join(' ');
}

export const removeDuplicates = (list: any[], filterKey: string) => {
  if (!list) return [];
  const seen = new Set();

  return list.filter(item => {
    if (seen.has(item[filterKey])) return false;
    seen.add(item[filterKey]);
    return true;
  });
}

export const getIsDoctor = (role: string) => {
  return role == 'SPECIALIST' || role == 'FELLOW' || role == 'RESIDENT';
}

export const validateForm = async (schema: any, data: any) => {
  try {
    await schema.validate(data, {
      abortEarly: false,
    });
  } catch (e) {
    if (e instanceof yup.ValidationError) {
      console.table(
        e.inner.map(err => ({
          path: err.path,
          message: err.message,
          value: err.value,
        }))
      );
    }
  }
}

export const getRegistryPatientInformationFields = (fields: CustomFieldType[]) => {
  const initialFields = [
    'patient_first_name',
    'patient_last_name',
    'gender',
    'date_of_birth',
    'institute',
    'mrn',
    'insurance',
  ];

  return fields
    .filter(field =>
      initialFields.includes(field.name)
    )
    .sort(
      (a, b) =>
        initialFields.indexOf(a.name) -
        initialFields.indexOf(b.name)
    );
};

export const getRegistryCustomFields = (fields: CustomFieldType[]) => {
  const initialFields = [
    'patient_first_name',
    'patient_last_name',
    'gender',
    'date_of_birth',
    'institute',
    'mrn',
    'insurance',
    'type',
    'date',
    'diagnosisCodes',
    'cptCodes',
    'role',
    'vendors',
    'findings',
    'complications',
    'outcome',
    'notes',
    'images',
  ];

  return fields
    .filter(field =>
      !initialFields.includes(field.name)
    )
    .sort(
      (a, b) =>
        initialFields.indexOf(a.name) -
        initialFields.indexOf(b.name)
    );
}

export const getCustomFieldsNames = (keys: string[]) => {
  const initialFields = [
    'patient_first_name',
    'patient_last_name',
    'gender',
    'date_of_birth',
    'institute',
    'mrn',
    'insurance',
    'type',
    'date',
    'diagnosisCodes',
    'cptCodes',
    'role',
    'vendors',
    'findings',
    'complications',
    'outcome',
    'notes',
    'images',
  ];

  return keys.forEach(key => !initialFields.includes(key));
}

export const getRegistryProcedureDetailsFields = (fields: CustomFieldType[]) => {
  const initialFields = [
    'type',
    'date',
    'role',
    'vendors',
    'findings',
    'complications',
    'outcome',
  ];

  return fields
    .filter(field =>
      initialFields.includes(field.name)
    )
    .sort(
      (a, b) =>
        initialFields.indexOf(a.name) -
        initialFields.indexOf(b.name)
    );
};

export const getRegistryProcedureFields = (fields: CustomFieldType[]) => {
  const registryPatientInformationFields = [
    'patient_first_name',
    'patient_last_name',
    'gender',
    'date_of_birth',
    'institute',
    'mrn',
    'insurance',
  ];

  return fields
    .filter(field =>
      registryPatientInformationFields.includes(field.name)
    )
    .sort(
      (a, b) =>
        registryPatientInformationFields.indexOf(a.name) -
        registryPatientInformationFields.indexOf(b.name)
    );
};